/* Live Monitor — cockpit-style real-time view of every bill flowing into Fundle.
   Green = mobile attached. Red = "Lost Opportunity" (anonymous walk-in / no mobile). */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { requestExport } from "@/lib/exportClient";
import { PageHeader, SectionHeading } from "./_shared";
import { fmtDateTimeISO, fmtMoney2 } from "@/lib/format";
import { toast } from "sonner";
import {
  Activity, ShoppingBag, AlertTriangle, CheckCircle2, RefreshCw, Pause, Play,
  Phone, PhoneOff, MapPin, Receipt, Filter, X, TrendingDown, Coins, Award, Calculator, Download,
} from "lucide-react";

const PALETTE = {
  burgundy: "#571326",
  indigo: "#1E3A8A",
  teal: "#0E7C7B",
  amber: "#B45309",
  rose: "#9F1239",
  emerald: "#047857",
};

// Row colour-coding by bill type — repeat (green), new (amber/gold), walk-in (rose),
// return (orange). Literal class strings so Tailwind's JIT keeps them.
const ROW_STYLES = {
  repeat:  { cls: "bg-emerald-50 hover:bg-emerald-100", border: "#047857" },
  new:     { cls: "bg-amber-50 hover:bg-amber-100",     border: "#D97706" },
  walk_in: { cls: "bg-rose-50 hover:bg-rose-100",       border: "#9F1239" },
  lost:    { cls: "bg-fuchsia-50 hover:bg-fuchsia-100", border: "#86198F" },
  return:  { cls: "bg-orange-50 hover:bg-orange-100",   border: "#EA580C" },
};
const rowKind = (r) =>
  r.is_return ? "return"
  : r.is_lost_customer ? "lost"
  : !r.has_mobile ? "walk_in"
  : r.customer_status === "new" ? "new"
  : "repeat";

// Today's calendar date in IST (YYYY-MM-DD) — drives the default "Today" window.
const istTodayIso = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());

export default function LiveMonitorPage() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [paused, setPaused] = useState(false);
  const [recalcing, setRecalcing] = useState(false);
  const [filters, setFilters] = useState({
    store_id: "",
    region: "",
    has_mobile: "all", // all | yes | no
    payment_mode: "",
    source: "",
    min_amount: "",
    max_amount: "",
    start_date: "",
    end_date: "",
  });
  const [statsWindow, setStatsWindow] = useState("today"); // default = Today (IST 00:00→23:59); the other relative windows remain in the dropdown
  const [drillRow, setDrillRow] = useState(null);
  const [page, setPage] = useState(1);
  const lastFetchRef = useRef(null);

  const load = () => {
    const hasRange = filters.start_date || filters.end_date;
    const params = { limit: 200 };
    const statsParams = {};
    if (hasRange) {
      // Explicit From/To date range overrides everything
      if (filters.start_date) { params.start_date = filters.start_date; statsParams.start_date = filters.start_date; }
      if (filters.end_date) { params.end_date = filters.end_date; statsParams.end_date = filters.end_date; }
    } else if (statsWindow === "today") {
      // Default: today's calendar day in IST (12 AM → 11:59 PM)
      const d = istTodayIso();
      params.start_date = d; params.end_date = d;
      statsParams.start_date = d; statsParams.end_date = d;
    } else {
      params.since_minutes = Number(statsWindow);
      statsParams.minutes = Number(statsWindow);
    }
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "start_date" || k === "end_date") return;
      if (v !== "" && v !== "all") params[k] = v;
    });
    return Promise.all([
      api.get("/live-monitor/transactions", { params }),
      api.get("/live-monitor/stats", { params: statsParams }),
    ]).then(([tx, st]) => {
      setRows(tx.data.rows || []);
      setStats(st.data || null);
      lastFetchRef.current = new Date();
    }).catch((e) => console.error("live-monitor load failed", e));
  };

  const loadStores = () => api.get("/stores").then((r) => setStores(r.data || [])).catch(() => {});

  // Streamed CSV export of the cockpit bills, honouring the current filters + window.
  const exportCsv = async () => {
    const hasRange = filters.start_date || filters.end_date;
    const p = {};
    if (hasRange) {
      if (filters.start_date) p.start_date = filters.start_date;
      if (filters.end_date) p.end_date = filters.end_date;
    } else if (statsWindow === "today") {
      const d = istTodayIso(); p.start_date = d; p.end_date = d;
    } else {
      p.since_minutes = Number(statsWindow);
    }
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "start_date" || k === "end_date") return;
      if (v !== "" && v !== "all") p[k] = v;
    });
    await requestExport({
      report_type: "live_monitor",
      params: p,
      label: "Live Folio Monitor",
      known_total: stats?.bills_total ?? null,
      filename: `live_monitor_${istTodayIso()}.csv`,
    });
  };

  // Re-credit points for bills that earned 0 (e.g. captured before the earn-engine fix).
  // Dry-run first to preview, then confirm to apply. Idempotent on the backend.
  const recalcPoints = async () => {
    setRecalcing(true);
    try {
      let preview = (await api.post("/live-monitor/recalc-points", { dry_run: true })).data;
      let ignoreFlag = false;

      if (!preview.eligible) {
        const s = preview.skipped || {};
        const reasons = [];
        if (s.loyalty_flag_off) reasons.push(`${s.loyalty_flag_off} marked loyalty-flag OFF`);
        if (s.below_min_or_zero_base) reasons.push(`${s.below_min_or_zero_base} below min-bill ₹${preview.min_bill_for_earn} (or no amount)`);
        if (s.customer_not_found) reasons.push(`${s.customer_not_found} have no matched customer`);
        if (s.computed_zero_rate) reasons.push(`${s.computed_zero_rate} compute 0 — earn rate/multiplier is 0`);
        const reasonStr = reasons.join("; ") || "all eligible sale bills already have points";

        if (s.loyalty_flag_off > 0) {
          const force = window.confirm(
            `No bills qualified normally.\nReason: ${reasonStr}.\n\n` +
            `${s.loyalty_flag_off} bill(s) are marked loyalty-flag OFF (older bills may have been stored wrongly by a previous version).\n\n` +
            `Force-credit these eligible sale bills regardless of the flag?\n(returns & below-min-bill are still skipped)`
          );
          if (!force) { toast.info(`No bills recalculated — ${reasonStr}.`); return; }
          ignoreFlag = true;
          preview = (await api.post("/live-monitor/recalc-points", { dry_run: true, ignore_loyalty_flag: true })).data;
          if (!preview.eligible) {
            toast.info(`Still 0 eligible. Likely earn rate/multiplier is 0, or all bills are below min-bill ₹${preview.min_bill_for_earn}. Fix Loyalty Rules, then retry.`);
            return;
          }
        } else {
          toast.info(`No bills recalculated — ${reasonStr}.`);
          return;
        }
      }

      const ok = window.confirm(
        `${preview.eligible} of today's Live-POS bill(s) currently have 0 points and qualify to earn ` +
        `${preview.total_points.toLocaleString()} points total${ignoreFlag ? " (ignoring loyalty flag)" : ""}.\n\nApply now and credit the customers?\n(Only today's Live-POS bills are recalculated — historic bills are never touched.)`
      );
      if (!ok) return;
      const res = (await api.post("/live-monitor/recalc-points", { dry_run: false, ignore_loyalty_flag: ignoreFlag })).data;
      toast.success(`Recalculated ${res.credited} bill(s) · credited ${res.total_points.toLocaleString()} points.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recalculation failed");
    } finally {
      setRecalcing(false);
    }
  };

  useEffect(() => { loadStores(); }, []);
  useEffect(() => {
    load();
    if (paused) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [paused, filters, statsWindow]);

  // Reset to first page only when the query (filters / window) changes — never on the 3s auto-refresh.
  useEffect(() => { setPage(1); }, [filters, statsWindow]);

  const clearFilters = () => setFilters({ store_id: "", region: "", has_mobile: "all", payment_mode: "", source: "", min_amount: "", max_amount: "", start_date: "", end_date: "" });
  const activeFilterCount = useMemo(() =>
    Object.values(filters).filter((v) => v !== "" && v !== "all").length, [filters]);
  // De-duplicate the store dropdown by NAME — production store master was bulk-uploaded
  // more than once, producing two docs (different ids/codes) per physical store, so each
  // store name appeared twice. Collapse them to a single option.
  const storeOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const s of stores) {
      const key = String(s.name || s.code || s.id || "").trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(s);
    }
    return out;
  }, [stores]);
  const windowEyebrow = (filters.start_date || filters.end_date)
    ? `${filters.start_date || "…"} → ${filters.end_date || "…"}`
    : statsWindow === "today" ? "TODAY (IST)" : `LAST ${statsWindow} MIN`;

  const PAGE_SIZE = 50;
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div data-testid="live-monitor-page">
      <PageHeader
        title="Live Folio Monitor"
        subtitle={`COCKPIT · ${rows.length} BILLS · AUTO-REFRESH ${paused ? "PAUSED" : "EVERY 3s"}`}
        actions={
          <>
            <button onClick={() => setPaused((p) => !p)} className="k-btn k-btn-outline k-btn-sm" data-testid="lm-toggle-pause">
              {paused ? <><Play className="w-3.5 h-3.5" /> Resume</> : <><Pause className="w-3.5 h-3.5" /> Pause</>}
            </button>
            <button onClick={recalcPoints} disabled={recalcing} className="k-btn k-btn-outline k-btn-sm" data-testid="lm-recalc-points">
              <Calculator className="w-3.5 h-3.5" /> {recalcing ? "Recalculating…" : "Recalc points"}
            </button>
            <button onClick={load} className="k-btn k-btn-outline k-btn-sm" data-testid="lm-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh now
            </button>
            <button onClick={exportCsv} className="k-btn k-btn-outline k-btn-sm" data-testid="lm-export">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </>
        }
      />

      <div className="p-8 space-y-6">
        {/* KPI strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-10 gap-3" data-testid="lm-kpis">
            <KPI label="Bills" value={(stats.bills_total || 0).toLocaleString()} icon={Receipt} color={PALETTE.burgundy} />
            <KPI label="Loyalty Bills" value={(stats.bills_with_mobile || 0).toLocaleString()} icon={Phone} color={PALETTE.emerald} testid="lm-kpi-loyalty-bills" />
            <KPI label="Repeat Bills" value={(stats.repeat_bills || 0).toLocaleString()} icon={Award} color={PALETTE.indigo} testid="lm-kpi-repeat-bills" />
            <KPI label="Lost Cust." value={(stats.bills_without_mobile || 0).toLocaleString()}
                  icon={PhoneOff} color={PALETTE.rose} testid="lm-kpi-lost" />
            <KPI label="Lost Purchase" value={fmtCurrency(stats.revenue_lost)} icon={ShoppingBag} color={PALETTE.rose} testid="lm-kpi-lost-rev" />
            <KPI label="Attach %" value={`${(stats.mobile_attach_rate_pct || 0).toFixed(1)}%`} icon={CheckCircle2} color={PALETTE.indigo} />
            <KPI label="Total Purchase" value={fmtCurrency(stats.revenue_total)} icon={ShoppingBag} color={PALETTE.teal} testid="lm-kpi-total-rev" />
            <KPI label="Loyalty Purchase" value={fmtCurrency(stats.loyalty_revenue)} icon={Award} color={PALETTE.emerald} testid="lm-kpi-loyalty-rev" />
            <KPI label="Pts Earned" value={(stats.points_earned || 0).toLocaleString()} icon={Coins} color={PALETTE.amber} />
            <KPI label="Returns" value={(stats.returns || 0).toLocaleString()} icon={TrendingDown} color={PALETTE.rose} />
          </div>
        )}

        {/* Filter row */}
        <div className="chart-card p-4" data-accent="indigo">
          <div className="flex flex-wrap items-end gap-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 self-center mr-1 flex items-center gap-1">
              <Filter className="w-3 h-3" /> FILTERS {activeFilterCount > 0 && <span className="pill pill-info ml-1">{activeFilterCount}</span>}
            </div>
            <Select label="Mobile" value={filters.has_mobile} onChange={(v) => setFilters({ ...filters, has_mobile: v })} options={[
              { value: "all", label: "All" },
              { value: "yes", label: "With mobile" },
              { value: "no", label: "Lost opp. only" },
            ]} testid="lm-fil-mobile" />
            <Select label="Store" value={filters.store_id} onChange={(v) => setFilters({ ...filters, store_id: v })} options={[
              { value: "", label: "All stores" },
              ...storeOptions.map((s) => ({ value: s.id, label: s.name || s.code })),
            ]} testid="lm-fil-store" />
            <Select label="Source" value={filters.source} onChange={(v) => setFilters({ ...filters, source: v })} options={[
              { value: "", label: "All" },
              { value: "pos_ewards", label: "POS (live)" },
              { value: "historic_upload", label: "Historical" },
              { value: "pos_auto", label: "POS auto-created" },
            ]} testid="lm-fil-source" />
            <Select label="Payment" value={filters.payment_mode} onChange={(v) => setFilters({ ...filters, payment_mode: v })} options={[
              { value: "", label: "All" },
              { value: "upi", label: "UPI" },
              { value: "card", label: "Card" },
              { value: "cash", label: "Cash" },
              { value: "wallet", label: "Wallet" },
            ]} testid="lm-fil-payment" />
            <NumInput label="Min ₹" value={filters.min_amount} onChange={(v) => setFilters({ ...filters, min_amount: v })} testid="lm-fil-min" />
            <NumInput label="Max ₹" value={filters.max_amount} onChange={(v) => setFilters({ ...filters, max_amount: v })} testid="lm-fil-max" />
            <DateInput label="From date" value={filters.start_date} onChange={(v) => setFilters({ ...filters, start_date: v })} testid="lm-fil-start-date" />
            <DateInput label="To date" value={filters.end_date} onChange={(v) => setFilters({ ...filters, end_date: v })} testid="lm-fil-end-date" />
            <Select label="Stats window" value={String(statsWindow)} onChange={(v) => setStatsWindow(v)} disabled={!!(filters.start_date || filters.end_date)} options={[
              { value: "today", label: "Today" },
              { value: "15", label: "Last 15m" },
              { value: "60", label: "Last 1h" },
              { value: "360", label: "Last 6h" },
              { value: "1440", label: "Last 24h" },
              { value: "10080", label: "Last 7d" },
              { value: "43200", label: "Last 30d" },
              { value: "129600", label: "Last 90d" },
              { value: "525600", label: "Last 365d" },
            ]} testid="lm-fil-window" />
            {(filters.start_date || filters.end_date) && (
              <div className="self-center text-[10px] text-amber-700 uppercase tracking-widest" data-testid="lm-range-active">Date range active · live window ignored</div>
            )}
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="k-btn k-btn-ghost k-btn-sm" data-testid="lm-clear-filters">
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Top stores */}
        {stats?.by_store_top10?.length > 0 && (
          <div className="chart-card p-5" data-accent="teal">
            <SectionHeading eyebrow={windowEyebrow} title="Top stores by revenue" accent="teal" />
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3 mt-3">
              {stats.by_store_top10.slice(0, 5).map((s) => (
                <div key={s.store_id || s.store_name} className="p-3 border border-black/10 bg-neutral-50" data-testid={`lm-top-store-${s.store_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{s.bills} BILLS · {s.attach_rate_pct}% ATTACH</div>
                    {s.store_code && <span className="font-mono text-[10px] text-teal-800 bg-teal-50 px-1.5 py-0.5 border border-teal-200" data-testid={`lm-top-store-code-${s.store_id}`}>{s.store_code}</span>}
                  </div>
                  <div className="font-display text-base truncate mt-1" title={s.store_name}>{s.store_name}</div>
                  <div className="font-mono text-sm mt-1">
                    {fmtMoney2(s.revenue || 0)}
                    {s.returns ? <span className="text-[10px] text-rose-600 ml-1">· {s.returns} ret</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live transactions table */}
        <div className="chart-card p-5" data-accent="burgundy" data-testid="lm-table-card">
          <SectionHeading eyebrow={`${rows.length} RECENT`} title="Bills as they arrive" accent="burgundy" />
          <div className="flex flex-wrap items-center gap-3 mt-2 mb-2 text-[10px] uppercase tracking-widest text-neutral-500" data-testid="lm-row-legend">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-100 border-l-2" style={{ borderColor: "#047857" }} /> Repeat</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-amber-100 border-l-2" style={{ borderColor: "#D97706" }} /> New</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-rose-100 border-l-2" style={{ borderColor: "#9F1239" }} /> Walk-in</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-fuchsia-100 border-l-2" style={{ borderColor: "#86198F" }} /> Lost Customer (invalid mobile)</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-orange-100 border-l-2" style={{ borderColor: "#EA580C" }} /> Return</span>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-2"></th>
                  <th>Bill Date · Time</th>
                  <th>Store</th>
                  <th>Loc Code</th>
                  <th>Bill #</th>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Mobile</th>
                  <th className="text-right">Bill Amt</th>
                  <th className="text-right">Pts Base</th>
                  <th className="text-right">Tax</th>
                  <th className="text-right">Discount</th>
                  <th className="text-right">Earn</th>
                  <th className="text-right">Redeem</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={15} className="py-10 text-center text-neutral-500 text-sm">Waiting for live bills…</td></tr>
                ) : pageRows.map((r) => {
                  const rk = ROW_STYLES[rowKind(r)];
                  return (
                  <tr key={r.id} onClick={() => setDrillRow(r)} className={`cursor-pointer ${rk.cls}`}
                       style={{ borderLeft: `4px solid ${rk.border}` }}
                       data-testid={`lm-row-${r.id}`}>
                    <td>
                      {r.has_mobile
                        ? <span className="inline-flex items-center" title="Mobile attached"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /></span>
                        : <span className="inline-flex items-center" title="LOST OPPORTUNITY — no customer mobile"><AlertTriangle className="w-3.5 h-3.5 text-rose-600" /></span>}
                    </td>
                    <td className="text-[11px] whitespace-nowrap">
                      <div>{fmtDateTimeISO(r.bill_date)}</div>
                      {r.received_at && (
                        <div className="text-[10px] text-neutral-400" title="When Fundle received this bill">
                          Recd {fmtDateTimeISO(r.received_at)}
                        </div>
                      )}
                    </td>
                    <td className="text-[12px]">
                      <div className="font-medium truncate max-w-[200px]" title={r.store_name}>{r.store_name || "—"}</div>
                      {r.city && <div className="text-[10px] text-neutral-500">{r.city}{r.zone ? ` · ${r.zone}` : ""}</div>}
                    </td>
                    <td className="font-mono text-[11px] text-neutral-700">{r.store_code || "—"}</td>
                    <td className="font-mono text-[11px]">{r.bill_number}</td>
                    <td className="text-[12px]">
                      {r.customer_name ? (
                        <div className="truncate max-w-[160px]">{r.customer_name}</div>
                      ) : r.has_mobile ? (
                        <span className="text-[11px] text-neutral-500 italic">Name unknown</span>
                      ) : (
                        <span className="text-[10px] text-rose-700 font-medium uppercase tracking-widest">LOST OPP.</span>
                      )}
                      {r.tier && <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{r.tier}</div>}
                    </td>
                    <td className="text-[10px] uppercase tracking-widest">
                      {r.is_return
                        ? <span className="pill pill-warning" data-testid={`lm-type-return-${r.bill_number}`}>Return</span>
                        : r.is_lost_customer
                          ? <span className="pill" style={{ background: "#FAE8FF", color: "#86198F", border: "1px solid #F0ABFC" }} data-testid={`lm-type-lost-${r.bill_number}`}>Lost Cust.</span>
                          : !r.has_mobile
                            ? <span className="pill pill-danger">Walk-in</span>
                            : r.customer_status === "new"
                              ? <span className="pill" style={{ background: "#FDE68A", color: "#92400E", border: "1px solid #FBBF24" }}>New</span>
                              : <span className="pill pill-success">Repeat</span>}
                    </td>
                    <td className="font-mono text-[12px]">
                      {r.customer_mobile ? (
                        <span className="text-emerald-700">{r.customer_mobile}</span>
                      ) : r.is_lost_customer && r.raw_mobile ? (
                        <span className="text-fuchsia-700 line-through" title="Invalid Indian mobile — no points given">{r.raw_mobile}</span>
                      ) : (
                        <span className="text-rose-700">—</span>
                      )}
                    </td>
                    <td className="text-right font-mono tabular-nums" data-testid={`lm-row-amount-${r.bill_number}`}>{fmtMoney2((r.bill_with_tax ?? r.net_amount ?? r.final_amount) || 0)}</td>
                    <td className="text-right font-mono tabular-nums text-emerald-700" data-testid={`lm-row-base-${r.bill_number}`}>{fmtMoney2((r.points_base ?? r.amount ?? r.net_amount) || 0)}</td>
                    <td className="text-right font-mono tabular-nums text-neutral-500">{fmtMoney2(r.tax_amount || 0)}</td>
                    <td className="text-right font-mono tabular-nums text-neutral-500">{fmtMoney2(r.discount_amount || 0)}</td>
                    <td className="text-right font-mono tabular-nums text-amber-700">{r.points_earned || 0}</td>
                    <td className="text-right font-mono tabular-nums text-indigo-700">{r.points_redeemed || 0}</td>
                    <td>
                      <SourcePill source={r.source} />
                      {r.is_return && <span className="pill pill-warning ml-1">RETURN</span>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-3 text-xs" data-testid="lm-pagination">
              <span className="text-neutral-500">
                Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, rows.length)} of {rows.length} bills
              </span>
              <div className="flex items-center gap-2">
                <button className="k-btn k-btn-sm k-btn-outline" disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="lm-prev-page">Prev</button>
                <span className="font-mono">Page {safePage} / {totalPages}</span>
                <button className="k-btn k-btn-sm k-btn-outline" disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="lm-next-page">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {drillRow && <BillDrillModal row={drillRow} onClose={() => setDrillRow(null)} />}
    </div>
  );
}


function KPI({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="kpi-card" style={{ borderLeftColor: color, borderLeftWidth: 3 }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1 font-medium flex items-center gap-1">
        <Icon className="w-3 h-3" style={{ color }} /> {label}
      </div>
      <div className="kpi-value font-mono text-neutral-900" title={typeof value === 'string' || typeof value === 'number' ? String(value) : undefined}>{value}</div>
    </div>
  );
}

function Select({ label, value, onChange, options, testid, disabled }) {
  return (
    <label className="block">
      <div className="text-neutral-500 uppercase tracking-widest text-[10px] mb-1">{label}</div>
      <select className="k-input k-input-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled} style={{ minWidth: 130, opacity: disabled ? 0.5 : 1 }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function DateInput({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-neutral-500 uppercase tracking-widest text-[10px] mb-1">{label}</div>
      <input type="date" className="k-input k-input-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} style={{ minWidth: 140 }} />
    </label>
  );
}

function NumInput({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-neutral-500 uppercase tracking-widest text-[10px] mb-1">{label}</div>
      <input type="number" className="k-input k-input-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} style={{ width: 100 }} placeholder="—" />
    </label>
  );
}

function SourcePill({ source }) {
  if (!source) return <span className="text-neutral-400">—</span>;
  const map = {
    pos_ewards: { bg: "#ECFDF5", color: "#047857", border: "#A7F3D0", label: "POS live" },
    pos_auto: { bg: "#ECFDF5", color: "#047857", border: "#A7F3D0", label: "POS" },
    historic_upload: { bg: "#FAE8FF", color: "#86198F", border: "#F5D0FE", label: "Historical" },
    pos_test_seed: { bg: "#DBEAFE", color: "#1E40AF", border: "#BFDBFE", label: "Test seed" },
  };
  const s = map[source] || { bg: "#F3F4F6", color: "#374151", border: "#E5E7EB", label: source };
  return <span className="pill" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{s.label}</span>;
}

function fmtCurrency(n) {
  if (!n && n !== 0) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString()}`;
}


function BillDrillModal({ row, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl" onClick={(e) => e.stopPropagation()} data-testid="bill-drill-modal">
        <div className="p-5 border-b border-black/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: row.has_mobile ? "#047857" : "#B91C1C" }}>
              {row.has_mobile ? "BILL DETAIL" : "LOST OPPORTUNITY"}
            </div>
            <h3 className="font-display text-2xl">{row.bill_number}</h3>
          </div>
          <button onClick={onClose} className="k-btn k-btn-ghost k-btn-sm" data-testid="bill-drill-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3 text-sm">
          <Field label="Bill Date" value={fmtDateTimeISO(row.bill_date)} />
          <Field label="Received" value={row.received_at ? fmtDateTimeISO(row.received_at) : "—"} />
          <Field label="Store" value={row.store_name} />
          <Field label="Customer" value={row.customer_name || (row.has_mobile ? "Name unknown" : "LOST OPP.")} />
          <Field label="Mobile" value={row.customer_mobile || "—"} valueClass={row.has_mobile ? "text-emerald-700" : "text-rose-700 font-medium"} />
          <Field label="Tier" value={row.tier || "—"} />
          <Field label="Current Points" value={row.current_points?.toLocaleString() || "—"} />
          <Field label="Gross" value={fmtMoney2(row.gross_amount || 0)} />
          <Field label="Points base (₹)" value={fmtMoney2((row.points_base ?? row.amount ?? row.net_amount) || 0)} valueClass="text-emerald-700 font-medium" />
          <Field label="Tax (GST)" value={fmtMoney2(row.tax_amount || 0)} />
          <Field label="Bill w/ tax" value={fmtMoney2((row.bill_with_tax ?? row.net_amount) || 0)} />
          <Field label="Discount" value={fmtMoney2(row.discount_amount || 0)} />
          <Field label="Net" value={fmtMoney2(row.net_amount || 0)} />
          <Field label="Final paid" value={fmtMoney2(row.final_amount || row.net_amount || 0)} />
          <Field label="Points Earned" value={(row.points_earned || 0).toLocaleString()} valueClass="text-amber-700 font-medium" />
          <Field label="Points Redeemed" value={(row.points_redeemed || 0).toLocaleString()} valueClass="text-indigo-700 font-medium" />
          <Field label="Items" value={row.items_count} />
          <Field label="Payment" value={row.payment_mode} />
          <Field label="Source" value={row.source} />
          {row.is_return && <Field label="Return" value="YES" valueClass="text-rose-700 font-medium" />}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, valueClass = "" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">{label}</div>
      <div className={`font-mono text-sm ${valueClass}`}>{value || "—"}</div>
    </div>
  );
}
